import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useScopeKey } from './useScopeKey.js';

function buildParams(filters = {}) {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.category) params.set('category', filters.category);
  if (filters.category_ids?.length) {
    const csv = Array.isArray(filters.category_ids)
      ? filters.category_ids.join(',')
      : filters.category_ids;
    if (csv) params.set('category_ids', csv);
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.bank_account_id) params.set('bank_account_id', filters.bank_account_id);
  if (filters.initiator_id) params.set('initiator_id', filters.initiator_id);
  if (filters.search && filters.search.length >= 2) params.set('search', filters.search);
  if (filters.unclassified) params.set('unclassified', 'true');
  if (filters.vat_missing) params.set('vat_missing', 'true');
  if (filters.page) params.set('page', filters.page);
  if (filters.per_page) params.set('per_page', filters.per_page);
  if (filters.sort) params.set('sort', filters.sort);
  const str = params.toString();
  return str ? `?${str}` : '';
}

export function useTransactions(filters = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['transactions', scopeKey, filters],
    queryFn: () => api.get(`/transactions${buildParams(filters)}`),
    staleTime: 30_000,
    enabled: scopeKey !== 'loading',
  });
}

export function useVatMissingCount() {
  return useQuery({
    queryKey: ['transactions', 'vat_missing', 'count'],
    queryFn: () => api.get('/transactions?vat_missing=true&per_page=1'),
    staleTime: 60_000,
    select: (data) => data?.total ?? (Array.isArray(data) ? data.length : 0),
  });
}

export function useUnclassifiedCount() {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['transactions', 'unclassified', 'count', scopeKey],
    queryFn: () => api.get('/transactions/unclassified?per_page=1'),
    staleTime: 30_000,
    enabled: scopeKey !== 'loading' && scopeKey !== 'group',
    select: (data) => data?.total ?? data?.pagination?.total ?? 0,
  });
}

export function usePatchTransactionCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, category_id }) =>
      api.patch(`/transactions/${id}/category`, { category_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      // Changer la catégorie d'une transaction modifie le cash-flow (répartition
      // par catégorie, écart budget…) — cf. mutation TVA qui invalide déjà ceci.
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

/**
 * Attribue une catégorie à un lot de transactions en une seule requête
 * (sélection multiple via le logo sur la page Transactions). Tout ou rien
 * côté backend : voir POST /v1/transactions/bulk-category.
 */
export function useBulkPatchTransactionCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transaction_ids, category_id }) =>
      api.post('/transactions/bulk-category', { transaction_ids, category_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function usePatchTransactionIntercompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, intercompany_entity_id }) =>
      api.post(`/transactions/${id}/intercompany`, { intercompany_entity_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      // Le marquage change l'élimination en vue groupe et le rapprochement « À payer ».
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
      qc.invalidateQueries({ queryKey: ['upcoming'] });
    },
  });
}

export function usePatchTransactionRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, recurrence_id }) =>
      api.post(`/transactions/${id}/recurrence`, { recurrence_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['recurrences'] });
    },
  });
}
